// Shared MapVision review constants. Kept together so the site symbols drawn
// on the map, the type filter panel and the key in the help modal can't drift
// apart.
//
// Where the symbols come from
// ---------------------------
// MapVision renders every site marker with its own PNG symbol; the image is
// chosen server-side by the site-symbol rules in MapVision's
// `/master/rules_list` (`{vTypeName, vSubTypeName, iOperation, rule, color}`
// rows, where `color` holds the `<img>` for the matching rule). The MapVision
// legend master list (`legendId -> legendImage`) names the shared symbols and
// gives the universal fallback: legend 24, "Default Site", `icon-site_4_6-dot.png`.
//
// The tiles we draw from (tegola `mapvision-sites`, see `etc/tegola.toml`)
// carry the site's own `vSiteIcon` (as `site_icon`) alongside `type_name`. A
// site whose `vSiteIcon` we have an asset for is drawn with that exact
// MapVision symbol; everything else falls back to its *type's* symbol below.
// The type symbol is the MapVision rule that applies to that site type with
// no sub-type / operation match, i.e. the rule MapVision itself falls back to
// for the type:
//
//   Mosquito Site  rules_list #140, rule "Default", no sub-type   (no history)
//   Catchbasins    rules_list #26,  rule "Default", no sub-type   (no history)
//   Parcel         rules_list #196, rule "Default", sub-type "Parcel"
//   Surveillance   rules_list #137, rule "Default", sub-type "Adult Collection"
//
// Types with no rule at all (Historical Service Request, Public Information,
// Yellowjacket, Calsurv, Lab, Research, Ground/Aerial Adulticiding, Ground
// Larviciding, Ticks, ...) resolve to the "Default Site" symbol, as do
// sub-types MapVision has no rule for (verified against the live MapVision
// `POST /map/technician mode=image_icon` per-site icon for sites of each type).
//
// The type PNGs live in `static/img/mapvision/` and the per-site icons in
// `static/img/mapvision/icons/`, copied verbatim from MapVision's `/images/`
// directory; the original MapVision filename is noted on each entry.
// Refreshing them means re-downloading from MapVision rather than redrawing.

import type { ExpressionSpecification } from "maplibre-gl";

/** MapLibre image id plus the URL of the MapVision symbol PNG to register. */
export interface MapvisionSiteSymbol {
	/** Image id used by the symbol layer's `icon-image` and `map.addImage`. */
	id: string;
	/** URL of the symbol PNG, served from `static/img/mapvision/`. */
	url: string;
}

/** MapVision's universal site symbol: legend 24, "Default Site". */
export const MAPVISION_DEFAULT_SITE_SYMBOL: MapvisionSiteSymbol = {
	id: "mapvision-symbol-default-site",
	url: "/static/img/mapvision/default-site.png", // icon-site_4_6-dot.png
};

/**
 * One MapVision symbol per site type, in the order the type filter panel lists
 * them. Every type drawn on the map is present so the filter panel and help
 * modal can key off the same table the layer paints from; unknown types fall
 * back to {@link MAPVISION_DEFAULT_SITE_SYMBOL}.
 */
export const MAPVISION_SITE_TYPE_SYMBOLS: Record<string, MapvisionSiteSymbol> =
	{
		// "CB Gray dark graybrown 20 pixel tall.png" — rules_list #26.
		Catchbasins: {
			id: "mapvision-symbol-catchbasin",
			url: "/static/img/mapvision/catchbasin-default.png",
		},
		// "drop in water rings transparent triangle gray 30 pixel tall2.png" —
		// rules_list #140.
		"Mosquito Site": {
			id: "mapvision-symbol-mosquito-site",
			url: "/static/img/mapvision/mosquito-site-default.png",
		},
		// "parcel gray black.png" — rules_list #196 (sub-type "Parcel").
		Parcel: {
			id: "mapvision-symbol-parcel",
			url: "/static/img/mapvision/parcel-default.png",
		},
		// "ad hoc camera thicker border forest green 40 pixel tall.png" —
		// rules_list #137 (sub-type "Adult Collection").
		Surveillance: {
			id: "mapvision-symbol-surveillance",
			url: "/static/img/mapvision/surveillance-default.png",
		},
		// No MapVision rule: drawn with the "Default Site" symbol.
		"Historical Service Request": MAPVISION_DEFAULT_SITE_SYMBOL,
		"Public Information": MAPVISION_DEFAULT_SITE_SYMBOL,
		"Ground Adulticiding": MAPVISION_DEFAULT_SITE_SYMBOL,
		"Aerial Adulticiding": MAPVISION_DEFAULT_SITE_SYMBOL,
		Lab: MAPVISION_DEFAULT_SITE_SYMBOL,
		Calsurv: MAPVISION_DEFAULT_SITE_SYMBOL,
		Research: MAPVISION_DEFAULT_SITE_SYMBOL,
		Other: MAPVISION_DEFAULT_SITE_SYMBOL,
		"Ground Larviciding": MAPVISION_DEFAULT_SITE_SYMBOL,
		Yellowjacket: MAPVISION_DEFAULT_SITE_SYMBOL,
	};

/**
 * MapVision's per-site symbols, keyed by the exact
 * `mapvision.site_mas."vSiteIcon"` value the tiles carry as `site_icon`.
 *
 * A site's `vSiteIcon` is the filename MapVision itself draws that site with,
 * so an entry here is more precise than the type table above: it separates
 * colours, sub-types and monitoring-cycle variants (6 month / 24 month) that
 * a type-wide fallback cannot. Every value is mirrored into
 * `static/img/mapvision/icons/` under a slug of the MapVision filename
 * (lowercased, runs of non-alphanumerics collapsed to "-"), copied verbatim.
 *
 * 67 of the 80 `vSiteIcon` values MapVision uses are mirrored; the 13 we
 * could not obtain from MapVision — 8 `parcel *` variants, `ad hoc camera
 * thicker border forest green 40 pixel tall.png`, `icon-black-dot.png`,
 * `icon-olive-dot.png`, `invasive watering can bigger bubble blue 40 pixel
 * tall.png` and `weekly trap icon purple 2 pow outline 50 pixel tall.png` —
 * have no entry and fall through to the type symbol in the layer's paint
 * expression.
 */
export const MAPVISION_SITE_ICONS: Record<string, string> = {
	"CB bold font blue white 23 pixel tall.png":
		"/static/img/mapvision/icons/cb-bold-font-blue-white-23-pixel-tall.png",
	"CB bold font green black 23 pixel tall.png":
		"/static/img/mapvision/icons/cb-bold-font-green-black-23-pixel-tall.png",
	"CB bold font green white 23 pixel tall.png":
		"/static/img/mapvision/icons/cb-bold-font-green-white-23-pixel-tall.png",
	"CB bold font orange black 23 pixel tall.png":
		"/static/img/mapvision/icons/cb-bold-font-orange-black-23-pixel-tall.png",
	"CB bold font orange white 23 pixel tall.png":
		"/static/img/mapvision/icons/cb-bold-font-orange-white-23-pixel-tall.png",
	"CB Gray dark graybrown 20 pixel tall.png":
		"/static/img/mapvision/icons/cb-gray-dark-graybrown-20-pixel-tall.png",
	"CB Inactive Black 8.png":
		"/static/img/mapvision/icons/cb-inactive-black-8.png",
	"drop in water rings transparent triangle 24 month orange 35 pixel tall.png":
		"/static/img/mapvision/icons/drop-in-water-rings-transparent-triangle-24-month-orange-35-pixel-tall.png",
	"drop in water rings transparent triangle 6 month orange 35 pixel tall.png":
		"/static/img/mapvision/icons/drop-in-water-rings-transparent-triangle-6-month-orange-35-pixel-tall.png",
	"drop in water rings transparent triangle blue white 32 pixel tall.png":
		"/static/img/mapvision/icons/drop-in-water-rings-transparent-triangle-blue-white-32-pixel-tall.png",
	"drop in water rings transparent triangle gray 30 pixel tall2.png":
		"/static/img/mapvision/icons/drop-in-water-rings-transparent-triangle-gray-30-pixel-tall2.png",
	"drop in water rings transparent triangle green black 32 pixel tall.png":
		"/static/img/mapvision/icons/drop-in-water-rings-transparent-triangle-green-black-32-pixel-tall.png",
	"drop in water rings transparent triangle green white 32 pixel tall.png":
		"/static/img/mapvision/icons/drop-in-water-rings-transparent-triangle-green-white-32-pixel-tall.png",
	"drop in water rings transparent triangle orange black 32 pixel tall.png":
		"/static/img/mapvision/icons/drop-in-water-rings-transparent-triangle-orange-black-32-pixel-tall.png",
	"drop in water rings transparent triangle orange white 32 pixel tall.png":
		"/static/img/mapvision/icons/drop-in-water-rings-transparent-triangle-orange-white-32-pixel-tall.png",
	"frog gray dark graybrown 35 pixel tall.png":
		"/static/img/mapvision/icons/frog-gray-dark-graybrown-35-pixel-tall.png",
	"frog orange 24 month dark graybrown 35 pixel tall.png":
		"/static/img/mapvision/icons/frog-orange-24-month-dark-graybrown-35-pixel-tall.png",
	"frog orange 6 month dark graybrown 35 pixel tall.png":
		"/static/img/mapvision/icons/frog-orange-6-month-dark-graybrown-35-pixel-tall.png",
	"goat.png": "/static/img/mapvision/icons/goat.png",
	"greenblack.png": "/static/img/mapvision/icons/greenblack.png",
	"greenwhite.png": "/static/img/mapvision/icons/greenwhite.png",
	"icon-green-dot.png": "/static/img/mapvision/icons/icon-green-dot.png",
	"icon-site_4_6-dot.png": "/static/img/mapvision/icons/icon-site-4-6-dot.png",
	"low area new squiggle blue white 50 pixel tall2.png":
		"/static/img/mapvision/icons/low-area-new-squiggle-blue-white-50-pixel-tall2.png",
	"low area new squiggle gray dark gray thick border 50 pixel tall2.png":
		"/static/img/mapvision/icons/low-area-new-squiggle-gray-dark-gray-thick-border-50-pixel-tall2.png",
	"low area new squiggle green black 50 pixel tall2.png":
		"/static/img/mapvision/icons/low-area-new-squiggle-green-black-50-pixel-tall2.png",
	"low area new squiggle green white 50 pixel tall2.png":
		"/static/img/mapvision/icons/low-area-new-squiggle-green-white-50-pixel-tall2.png",
	"low area new squiggle orange 24 month black 45 pixel tall.png":
		"/static/img/mapvision/icons/low-area-new-squiggle-orange-24-month-black-45-pixel-tall.png",
	"low area new squiggle orange 6 month black 45 pixel tall.png":
		"/static/img/mapvision/icons/low-area-new-squiggle-orange-6-month-black-45-pixel-tall.png",
	"low area new squiggle orange black 50 pixel tall2.png":
		"/static/img/mapvision/icons/low-area-new-squiggle-orange-black-50-pixel-tall2.png",
	"low area new squiggle orange white 50 pixel tall2.png":
		"/static/img/mapvision/icons/low-area-new-squiggle-orange-white-50-pixel-tall2.png",
	"my water reeds hex 24 orange dark graybrown 40 pixel tall.png":
		"/static/img/mapvision/icons/my-water-reeds-hex-24-orange-dark-graybrown-40-pixel-tall.png",
	"my water reeds hex green white 40 pixel tall.png":
		"/static/img/mapvision/icons/my-water-reeds-hex-green-white-40-pixel-tall.png",
	"my water reeds hex orange white 40 pixel tall.png":
		"/static/img/mapvision/icons/my-water-reeds-hex-orange-white-40-pixel-tall.png",
	"my water reeds thick hex blue white 40 pixel tall.png":
		"/static/img/mapvision/icons/my-water-reeds-thick-hex-blue-white-40-pixel-tall.png",
	"my water reeds thick hex gray dark graybrown 40 pixel tall.png":
		"/static/img/mapvision/icons/my-water-reeds-thick-hex-gray-dark-graybrown-40-pixel-tall.png",
	"my water reeds thick hex green black 40 pixel tall.png":
		"/static/img/mapvision/icons/my-water-reeds-thick-hex-green-black-40-pixel-tall.png",
	"my water reeds thick hex orange black 40 pixel tall.png":
		"/static/img/mapvision/icons/my-water-reeds-thick-hex-orange-black-40-pixel-tall.png",
	"my water thick reeds hex orange 6 month 40 pixel tall.png":
		"/static/img/mapvision/icons/my-water-thick-reeds-hex-orange-6-month-40-pixel-tall.png",
	"orangeblack.png": "/static/img/mapvision/icons/orangeblack.png",
	"orangewhite.png": "/static/img/mapvision/icons/orangewhite.png",
	"pond icon blue white 35 pixel tall centered.png":
		"/static/img/mapvision/icons/pond-icon-blue-white-35-pixel-tall-centered.png",
	"pond icon gray dark graybrown 35 pixel tall centered.png":
		"/static/img/mapvision/icons/pond-icon-gray-dark-graybrown-35-pixel-tall-centered.png",
	"pond icon green black 37 pixel tall centered.png":
		"/static/img/mapvision/icons/pond-icon-green-black-37-pixel-tall-centered.png",
	"pond icon green white 35 pixel tall centered.png":
		"/static/img/mapvision/icons/pond-icon-green-white-35-pixel-tall-centered.png",
	"pond icon orange 24 dark graybrown 45 pixel tall.png":
		"/static/img/mapvision/icons/pond-icon-orange-24-dark-graybrown-45-pixel-tall.png",
	"pond icon orange 6 month dark graybrown v4 45 pixel tall.png":
		"/static/img/mapvision/icons/pond-icon-orange-6-month-dark-graybrown-v4-45-pixel-tall.png",
	"pond icon orange black 35 pixel tall centered.png":
		"/static/img/mapvision/icons/pond-icon-orange-black-35-pixel-tall-centered.png",
	"pond icon orange white 35 pixel tall centered.png":
		"/static/img/mapvision/icons/pond-icon-orange-white-35-pixel-tall-centered.png",
	"pool blue white 40 pixel tall.png":
		"/static/img/mapvision/icons/pool-blue-white-40-pixel-tall.png",
	"pool gray dark gray 40 pixel tall.png":
		"/static/img/mapvision/icons/pool-gray-dark-gray-40-pixel-tall.png",
	"pool green black 40 pixel tall.png":
		"/static/img/mapvision/icons/pool-green-black-40-pixel-tall.png",
	"pool green white 40 pixel tall.png":
		"/static/img/mapvision/icons/pool-green-white-40-pixel-tall.png",
	"pool orange 24 month 45 pixel tall.png":
		"/static/img/mapvision/icons/pool-orange-24-month-45-pixel-tall.png",
	"pool orange 6 month 45 pixel tall.png":
		"/static/img/mapvision/icons/pool-orange-6-month-45-pixel-tall.png",
	"pool orange black 40 pixel tall.png":
		"/static/img/mapvision/icons/pool-orange-black-40-pixel-tall.png",
	"pool orange white 40 pixel tall.png":
		"/static/img/mapvision/icons/pool-orange-white-40-pixel-tall.png",
	"site_3_1.png": "/static/img/mapvision/icons/site-3-1.png",
	"site_3_2.png": "/static/img/mapvision/icons/site-3-2.png",
	"water trough5 24 month orange 21 pixel tall.png":
		"/static/img/mapvision/icons/water-trough5-24-month-orange-21-pixel-tall.png",
	"water trough5 6 month orange 21 pixel tall.png":
		"/static/img/mapvision/icons/water-trough5-6-month-orange-21-pixel-tall.png",
	"water trough5 blue white 21 pixel tall.png":
		"/static/img/mapvision/icons/water-trough5-blue-white-21-pixel-tall.png",
	"water trough5 gray 20 pixel tall.png":
		"/static/img/mapvision/icons/water-trough5-gray-20-pixel-tall.png",
	"water trough5 green black 21 pixel tall.png":
		"/static/img/mapvision/icons/water-trough5-green-black-21-pixel-tall.png",
	"water trough5 green white 21 pixel tall.png":
		"/static/img/mapvision/icons/water-trough5-green-white-21-pixel-tall.png",
	"water trough5 orange black 21 pixel tall.png":
		"/static/img/mapvision/icons/water-trough5-orange-black-21-pixel-tall.png",
	"water trough5 orange white 21 pixel tall.png":
		"/static/img/mapvision/icons/water-trough5-orange-white-21-pixel-tall.png",
};

/**
 * MapLibre image id for the per-site icon asset at `url`. The map component
 * registers every icon under this id so the layer's paint expression can name
 * it.
 */
export function mapvisionIconId(url: string): string {
	const slug = url.slice(url.lastIndexOf("/") + 1, -".png".length);
	return `mapvision-icon-${slug}`;
}

/**
 * Every distinct symbol the map has to register before the layer paints, in a
 * stable order (first appearance wins): the type symbols the filter panel and
 * help modal key off, the per-site icons the layer prefers, then the
 * universal default.
 */
export const MAPVISION_SITE_SYMBOLS: MapvisionSiteSymbol[] = (() => {
	const seen = new Set<string>();
	const symbols: MapvisionSiteSymbol[] = [];
	for (const symbol of [
		...Object.values(MAPVISION_SITE_TYPE_SYMBOLS),
		...Object.values(MAPVISION_SITE_ICONS).map((url) => ({
			id: mapvisionIconId(url),
			url,
		})),
		MAPVISION_DEFAULT_SITE_SYMBOL,
	]) {
		if (seen.has(symbol.id)) continue;
		seen.add(symbol.id);
		symbols.push(symbol);
	}
	return symbols;
})();

/**
 * The site layer's `icon-image` expression: a site's own mirrored symbol when
 * the tile carries a `site_icon` we have an asset for, otherwise its type's
 * symbol, otherwise MapVision's universal "Default Site" symbol. Unknown or
 * absent values — the 13 icons we could not mirror, NULL/empty `vSiteIcon`,
 * types outside the table — fall through rather than painting nothing.
 */
export function mapvisionSiteIconImage(): ExpressionSpecification {
	const typeMatch: unknown[] = ["match", ["get", "type_name"]];
	for (const [type, symbol] of Object.entries(MAPVISION_SITE_TYPE_SYMBOLS)) {
		typeMatch.push(type, symbol.id);
	}
	typeMatch.push(MAPVISION_DEFAULT_SITE_SYMBOL.id);

	const iconMatch: unknown[] = ["match", ["get", "site_icon"]];
	for (const [icon, url] of Object.entries(MAPVISION_SITE_ICONS)) {
		iconMatch.push(icon, mapvisionIconId(url));
	}
	iconMatch.push(typeMatch);

	return iconMatch as ExpressionSpecification;
}

/** The symbol a site of `type` is drawn with; unknown types get the default. */
export function mapvisionSymbolForType(
	type: string | null | undefined,
): MapvisionSiteSymbol {
	return (
		(type && MAPVISION_SITE_TYPE_SYMBOLS[type]) || MAPVISION_DEFAULT_SITE_SYMBOL
	);
}
